import type { Awaitable, } from "@vueuse/core"
import type {
  CascaderComponentProps,
  CheckboxGroupProps,
  RadioGroupProps,
  SelectProps,
} from "element-plus"

interface FieldConfig {
  label: string
  value: string
  disabled?: string
  children?: string
}

export interface ApiConfig<P = any, R = any,> {
  config?: FieldConfig
  api: (params?: P,) => Promise<R | undefined>
}

export interface OptionItem {
  children?: OptionItem[]
  disabled?: boolean
  label?: string | number
  value?: string | number
  [name: string]: any
}

export type Options = OptionItem[] | string[] | number[]

export interface BaseProps<P = any, R = any,> {
  cacheKey?: string
  /**
   * 接口配置
   */
  apiConfig?: ApiConfig<P, R>
  /**
   * 是否立即执行
   */
  immediate?: boolean
  /**
   * 接口请求参数
   */
  params?: P
  /**
   * 接口请求之前的回调函数，参数为传入的参数，根据返回结果判断是否进行请求
   */
  beforeFetch?: (params: P,) => Awaitable<boolean>
  /**
   * 接口请求之后的回调函数，返回结果将作为 options 传递给formatOptions
   */
  afterFetch?: (res?: R,) => Awaitable<any[]>
  /**
   * 是否缓存数据
   */
  cacheData?: boolean
}

// Re-export Props as BaseProps for compatibility/clarity if needed, or just BaseProps.
// But the components will use specific props.
export type Props<P = any, R = any,> = BaseProps<P, R>

export type ApiSelectProps<P = any, R = any,> = BaseProps<P, R> & Partial<SelectProps>
export type ApiCascaderProps<P = any, R = any,> = BaseProps<P, R> & Partial<CascaderComponentProps>
export type ApiSelectV2Props<P = any, R = any,> = BaseProps<P, R> & Partial<SelectProps>
export type ApiCheckboxGroupProps<P = any, R = any,> = BaseProps<P, R> & Partial<CheckboxGroupProps>
export type ApiRadioGroupProps<P = any, R = any,> = BaseProps<P, R> & Partial<RadioGroupProps>

export const basePropsKeys: (keyof BaseProps)[] = [
  "cacheKey",
  "apiConfig",
  "immediate",
  "params",
  "beforeFetch",
  "afterFetch",
  "cacheData",
]

export function buildDefaultBaseProps<P, R,>() {
  return {
    cacheKey: {
      type: String,
      default: undefined,
    },
    params: {
      type: Object as PropType<P>,
      default: () => ({}),
    },
    apiConfig: {
      type: Object as PropType<ApiConfig<P, R>>,
      default: undefined,
    },
    immediate: {
      type: Boolean,
      default: true,
    },
    beforeFetch: {
      type: Function as PropType<(params: P,) => Awaitable<boolean>>,
      default: () => () => true,
    },
    afterFetch: {
      type: Function as PropType<(res?: R,) => Awaitable<any[]>>,
      default: (res: any,) => {
        return res?.data ?? (Array.isArray(res,) ? res : [])
      },
    },
    cacheData: {
      type: Boolean,
      default: true,
    },
  }
}

export const defaultProps = {
  immediate: true,
  beforeFetch: () => true,
  afterFetch: (res: any,) => {
    return res?.data ?? (Array.isArray(res,) ? res : [])
  },
  apiConfig: undefined,
  cacheData: true,
}
